import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  ResponsiveContainer,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  AreaChart, Area
} from 'recharts';

import '../css/styles.css';
import '../css/admin.css';
import '../css/user-details.css';

import {
  ALLOWED_ROLES,
  createAdminUser,
  getActiveUsersCount,
  measureApiResponseTime,
  getStatsUsersCount,
  getStatsOrdersCount,
  getStatsReviewsCount,
  getStatsOrdersGraph,
  getStatsUsersGraph,
  getStatsReviewsGraph
} from '../api';
import Icon from '../components/Icon';
import PremiumSelect from '../components/PremiumSelect';
import CategoryStats from '../components/CategoryStats';
import AdminPushForm from './AdminPushForm';
import AdminPromoCodes from './AdminPromoCodes';
import AdminNewsBanners from './AdminNewsBanners';
import AdminMarketplace from './AdminMarketplace';
import AdminPublicFiles from './AdminPublicFiles';
import AdminDeleteUser from './AdminDeleteUser';

const UI_TO_STATS_PERIOD = {
  day: 'DAY',
  week: 'WEEK',
  month: 'MONTH',
  all: 'ALL'
};

const toApiPeriod = (uiKey) => UI_TO_STATS_PERIOD[uiKey] || 'WEEK';

const DEFAULT_CREATE_EMPLOYEE_PHONE = '+373';

const mergeOverviewPoints = (ordersPts, usersPts, reviewsPts) => {
  const usersMap = new Map((usersPts || []).map((p) => [p.label, Number(p.value) || 0]));
  const reviewsMap = new Map((reviewsPts || []).map((p) => [p.label, Number(p.value) || 0]));
  const o = ordersPts || [];
  if (!o.length && !(usersPts || []).length && !(reviewsPts || []).length) {
    return [{ name: '—', orders: 0, users: 0, comments: 0 }];
  }
  if (!o.length) {
    const labels = [...new Set([...(usersPts || []).map((p) => p.label), ...(reviewsPts || []).map((p) => p.label)])];
    return labels.map((label) => ({
      name: label,
      orders: 0,
      users: usersMap.get(label) ?? 0,
      comments: reviewsMap.get(label) ?? 0
    }));
  }
  return o.map((p) => ({
    name: p.label,
    orders: Number(p.value) || 0,
    users: usersMap.get(p.label) ?? 0,
    comments: reviewsMap.get(p.label) ?? 0
  }));
};

const statCountFetchers = {
  orders: (ui) => getStatsOrdersCount(toApiPeriod(ui)),
  users: (ui) => getStatsUsersCount(toApiPeriod(ui)),
  reviews: (ui) => getStatsReviewsCount(toApiPeriod(ui))
};

// Кастомный тултип с эффектом Glassmorphism
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        padding: '16px',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        borderRadius: '16px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)'
      }}>
        {label && <p style={{ margin: '0 0 12px 0', fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>{label}</p>}
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '6px 0', fontSize: '0.95rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              backgroundColor: entry.color || 'var(--accent-primary)',
              boxShadow: `0 0 8px ${entry.color}80`
            }}></span>
            <span style={{ fontWeight: 500 }}>{entry.name}:</span>
            <span style={{ fontWeight: 800, marginLeft: 'auto', color: '#0f172a' }}>{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AnimatedNumber = ({ value }) => {
  const numericValue = useMemo(() => {
    if (typeof value === 'string') return parseFloat(value.replace(/,/g, ''));
    return value;
  }, [value]);

  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest).toLocaleString());

  useEffect(() => {
    const controls = animate(count, numericValue, { duration: 0.8, ease: "easeOut" });
    return controls.stop;
  }, [numericValue, count]);

  return <motion.h2 className="card-value">{rounded}</motion.h2>;
};

const StatCard = ({ title, icon, color, entity }) => {
  const [period, setPeriod] = useState('week');
  const [displayValue, setDisplayValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const n = await statCountFetchers[entity](period);
        if (!cancelled) setDisplayValue(Number(n) || 0);
      } catch (e) {
        console.error('StatCard count', entity, e);
        if (!cancelled) setDisplayValue(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entity, period]);

  return (
    <div className="admin-card-premium">
      <div className="card-header">
        <h3 className="card-title">
          <span className="icon-circle-premium" style={{ color: color }}>
            <Icon name={icon} size={16} />
          </span>
          {title}
        </h3>
      </div>
      <div className="card-divider" />
      <div className="card-body">
        <div className="admin-card-metrics-left">
          {loading ? (
            <h2 className="card-value" style={{ margin: 0 }}>…</h2>
          ) : (
            <AnimatedNumber value={displayValue} />
          )}
        </div>

        <div className="period-selector vertical">
          {[
            { key: 'day', label: t('admin.period.dayShort', 'Д') },
            { key: 'week', label: t('admin.period.weekShort', 'Н') },
            { key: 'month', label: t('admin.period.monthShort', 'М') },
            { key: 'all', label: t('admin.period.allShort', 'В') }
          ].map((p) => (
            <button
              key={p.key}
              type="button"
              className={`period-pill ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}
              title={t(`admin.period.${p.key}`, p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeUsersCount, setActiveUsersCount] = useState(null);
  const [formData, setFormData] = useState({
    phoneNumber: DEFAULT_CREATE_EMPLOYEE_PHONE,
    email: '',
    password: '',
    username: '',
    userInterface: 'WEB',
    role: ALLOWED_ROLES?.SUPPORT || 'SUPPORT',
  });
  const [createEmployeePasswordVisible, setCreateEmployeePasswordVisible] = useState(false);
  const [createEmployeeConfirmOpen, setCreateEmployeeConfirmOpen] = useState(false);
  const [createEmployeeSubmitting, setCreateEmployeeSubmitting] = useState(false);
  const [createEmployeeFormError, setCreateEmployeeFormError] = useState(null);
  const [createEmployeeApiError, setCreateEmployeeApiError] = useState(null);
  const [createEmployeeSuccess, setCreateEmployeeSuccess] = useState(false);
  const [apiResponseTime, setApiResponseTime] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const normalizedRole = storedUser?.role?.toString().trim().toUpperCase();
  const isModeratorOnly = normalizedRole === ALLOWED_ROLES.MODERATOR;
  const isDev = normalizedRole === ALLOWED_ROLES.DEV;
  // DEV стоит над ADMIN и обладает всеми правами админа, поэтому там,
  // где раньше проверялся только ADMIN, теперь допускается ADMIN или DEV.
  const isAdmin = normalizedRole === ALLOWED_ROLES.ADMIN || isDev;

  // Роли, которые текущий сотрудник вправе назначать при создании нового:
  //  - DEV: любые роли, включая ADMIN и DEV;
  //  - ADMIN: только SUPPORT и MODERATOR (создавать ADMIN/DEV больше нельзя).
  const creatableRoles = useMemo(() => {
    if (isDev) {
      return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
    }
    if (isAdmin) {
      return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR];
    }
    return [ALLOWED_ROLES.SUPPORT];
  }, [isDev, isAdmin]);

  const adminSidebarTabs = useMemo(() => {
    const tabs = [{ id: 'stats', icon: 'bar-chart-2', label: t('admin.dashboard.stats') }];
    if (!isModeratorOnly) {
      tabs.push(
        { id: 'push', icon: 'bell', label: t('admin.dashboard.notifications') },
        { id: 'create', icon: 'user-plus', label: t('admin.dashboard.createEmployee') }
      );
    }
    if (isAdmin) {
      tabs.push(
        { id: 'promo', icon: 'tag', label: t('admin.dashboard.promoCodes') },
        // Покупки маркетплейса доступны ADMIN и DEV; управление сервисами — только DEV
        // (см. гейт внутри AdminMarketplace).
        { id: 'marketplace', icon: 'shopping-cart', label: t('admin.dashboard.marketplace') }
      );
    }
    // Баннеры, публичные файлы и удаление пользователей эксклюзивны для DEV.
    if (isDev) {
      tabs.push(
        { id: 'banners', icon: 'image', label: t('admin.dashboard.banners') },
        { id: 'public-files', icon: 'upload', label: t('admin.dashboard.publicFiles') },
        { id: 'delete-user', icon: 'trash-2', label: t('admin.dashboard.deleteUser') }
      );
    }
    return tabs;
  }, [isModeratorOnly, isAdmin, isDev, t]);

  // Заголовок страницы: какой раздел выбран в общей боковой панели.
  const currentTabMeta = useMemo(
    () => adminSidebarTabs.find((tab) => tab.id === activeTab) || adminSidebarTabs[0],
    [adminSidebarTabs, activeTab]
  );

  // States for interactive charts (периоды → API StatsPeriod)
  const [chartPeriod1, setChartPeriod1] = useState('week');
  const [chartPeriod2, setChartPeriod2] = useState('day');
  const [chartPeriod3, setChartPeriod3] = useState('week');
  const [chartPeriod4, setChartPeriod4] = useState('week');

  const [overviewChartData, setOverviewChartData] = useState([{ name: '—', orders: 0, users: 0, comments: 0 }]);
  const [ordersChartData, setOrdersChartData] = useState([{ name: '—', orders: 0 }]);
  const [usersChartData, setUsersChartData] = useState([{ name: '—', users: 0 }]);
  const [commentsChartData, setCommentsChartData] = useState([{ name: '—', comments: 0 }]);

  // Recharts ResponsiveContainer иногда отдаёт 0×0 после быстрого скролла и перестаёт рисовать до resize
  useEffect(() => {
    if (activeTab !== 'stats') return undefined;
    let timeoutId;
    const onScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 150);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timeoutId);
    };
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    const api = toApiPeriod(chartPeriod1);
    (async () => {
      try {
        const [og, ug, rg] = await Promise.all([
          getStatsOrdersGraph(api),
          getStatsUsersGraph(api),
          getStatsReviewsGraph(api)
        ]);
        if (!cancelled) setOverviewChartData(mergeOverviewPoints(og, ug, rg));
      } catch (e) {
        console.error('Overview stats graph', e);
        if (!cancelled) setOverviewChartData([{ name: '—', orders: 0, users: 0, comments: 0 }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chartPeriod1]);

  useEffect(() => {
    let cancelled = false;
    const api = toApiPeriod(chartPeriod2);
    (async () => {
      try {
        const pts = await getStatsOrdersGraph(api);
        const mapped = (pts || []).map((p) => ({ name: p.label, orders: Number(p.value) || 0 }));
        if (!cancelled) setOrdersChartData(mapped.length ? mapped : [{ name: '—', orders: 0 }]);
      } catch (e) {
        console.error('Orders stats graph', e);
        if (!cancelled) setOrdersChartData([{ name: '—', orders: 0 }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chartPeriod2]);

  useEffect(() => {
    let cancelled = false;
    const api = toApiPeriod(chartPeriod3);
    (async () => {
      try {
        const pts = await getStatsUsersGraph(api);
        const mapped = (pts || []).map((p) => ({ name: p.label, users: Number(p.value) || 0 }));
        if (!cancelled) setUsersChartData(mapped.length ? mapped : [{ name: '—', users: 0 }]);
      } catch (e) {
        console.error('Users stats graph', e);
        if (!cancelled) setUsersChartData([{ name: '—', users: 0 }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chartPeriod3]);

  useEffect(() => {
    let cancelled = false;
    const api = toApiPeriod(chartPeriod4);
    (async () => {
      try {
        const pts = await getStatsReviewsGraph(api);
        const mapped = (pts || []).map((p) => ({ name: p.label, comments: Number(p.value) || 0 }));
        if (!cancelled) setCommentsChartData(mapped.length ? mapped : [{ name: '—', comments: 0 }]);
      } catch (e) {
        console.error('Reviews stats graph', e);
        if (!cancelled) setCommentsChartData([{ name: '—', comments: 0 }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chartPeriod4]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (isModeratorOnly) {
      if (tab && tab !== 'stats') {
        navigate('/admin?tab=stats', { replace: true });
        setActiveTab('stats');
        return;
      }
      setActiveTab('stats');
      return;
    }
    if (tab === 'promo' && !isAdmin) {
      navigate('/admin?tab=stats', { replace: true });
      setActiveTab('stats');
      return;
    }
    if (tab === 'marketplace' && !isAdmin) {
      navigate('/admin?tab=stats', { replace: true });
      setActiveTab('stats');
      return;
    }
    if ((tab === 'banners' || tab === 'public-files' || tab === 'delete-user') && !isDev) {
      navigate('/admin?tab=stats', { replace: true });
      setActiveTab('stats');
      return;
    }
    if (tab && ['stats', 'push', 'create', 'promo', 'banners', 'marketplace', 'public-files', 'delete-user'].includes(tab)) {
      setActiveTab(tab);
    } else if (params.get('openForm') === 'true') {
      setActiveTab('create');
    }
  }, [location.search, isModeratorOnly, isAdmin, isDev, navigate]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoadingStats(true);
        let usersCount = 142;
        let responseTime = 45;
        try {
          if (typeof getActiveUsersCount === 'function') usersCount = await getActiveUsersCount();
          if (typeof measureApiResponseTime === 'function') responseTime = await measureApiResponseTime();
        } catch (e) {
          console.warn('API functions not available, using mock data');
        }
        setActiveUsersCount(usersCount);
        setApiResponseTime(responseTime);
      } catch (error) {
        console.error('Stats error:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const field =
      name === 'newEmployeeFullName'
        ? 'username'
        : name === 'newEmployeePassword'
          ? 'password'
          : name;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setCreateEmployeeFormError(null);
    setCreateEmployeeApiError(null);
  };

  // Не даём Chrome связать поля с логином текущего пользователя + убираем пароль при уходе с вкладки
  useEffect(() => {
    if (activeTab !== 'create') {
      setFormData((prev) => ({ ...prev, password: '' }));
      setCreateEmployeePasswordVisible(false);
      setCreateEmployeeConfirmOpen(false);
      setCreateEmployeeApiError(null);
    }
  }, [activeTab]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setCreateEmployeeFormError(null);
    setCreateEmployeeApiError(null);

    const username = formData.username.trim();
    if (!username) {
      setCreateEmployeeFormError(t('admin.form.errorName'));
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setCreateEmployeeFormError(t('admin.form.errorPassword'));
      return;
    }
    const digits = (formData.phoneNumber || '').replace(/\D/g, '');
    if (digits.length < 8) {
      setCreateEmployeeFormError(t('admin.form.errorPhone'));
      return;
    }
    const em = (formData.email || '').trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setCreateEmployeeFormError(t('admin.form.errorEmailFormat'));
      return;
    }
    // Защита: нельзя создать сотрудника с ролью выше собственных полномочий
    // (например ADMIN, пытающийся выдать ADMIN/DEV).
    if (!creatableRoles.includes(formData.role)) {
      setCreateEmployeeFormError(t('admin.form.errorRoleForbidden'));
      return;
    }

    setCreateEmployeeConfirmOpen(true);
  };

  const handleCloseCreateConfirm = () => {
    if (createEmployeeSubmitting) return;
    setCreateEmployeeConfirmOpen(false);
    setCreateEmployeeApiError(null);
  };

  const handleConfirmCreateEmployee = async () => {
    setCreateEmployeeSubmitting(true);
    setCreateEmployeeApiError(null);
    try {
      await createAdminUser({
        phoneNumber: formData.phoneNumber.trim(),
        password: formData.password,
        username: formData.username.trim(),
        userInterface: formData.userInterface,
        role: formData.role,
        email: (formData.email || '').trim() || undefined,
      });
      setCreateEmployeeConfirmOpen(false);
      setFormData({
        phoneNumber: DEFAULT_CREATE_EMPLOYEE_PHONE,
        email: '',
        password: '',
        username: '',
        userInterface: 'WEB',
        role: ALLOWED_ROLES?.SUPPORT || 'SUPPORT',
      });
      setCreateEmployeeSuccess(true);
      window.setTimeout(() => setCreateEmployeeSuccess(false), 6000);
    } catch (err) {
      setCreateEmployeeApiError(err?.message || t('admin.form.createError'));
    } finally {
      setCreateEmployeeSubmitting(false);
    }
  };

  return (
    <div className="user-details-page admin-dashboard-root">
      <div className={`user-profile-dashboard no-side ${activeTab !== 'stats' ? 'no-stats' : ''}`}>
        {/* Разделы админки вынесены в общую боковую панель приложения */}
        <header className="admin-page-head">
          <div className="admin-page-icon">
            <Icon name={currentTabMeta.icon} size={22} />
          </div>
          <div>
            <h1 className="admin-page-title">{currentTabMeta.label}</h1>
          </div>
        </header>


        <main className="profile-dashboard-main">
          <AnimatePresence mode="wait">
            {activeTab === 'stats' && (
              <motion.div
                key="stats"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="bento-grid-layout">
                  {/* График: Сводка (Overview) */}
                  <div className="bento-card bento-large" style={{ gridColumn: 'span 2' }}>
                    <div className="card-header">
                      <h3 className="card-title">
                        <div className="card-icon-circle"><Icon name="trending-up" size={14} /></div>
                        {t('admin.dashboard.charts.overview')}
                      </h3>
                      <div className="period-selector">
                        {['day', 'week', 'month', 'all'].map(p => (
                          <button
                            key={p}
                            className={`period-pill ${chartPeriod1 === p ? 'active' : ''}`}
                            onClick={() => setChartPeriod1(p)}
                          >
                            {t(`admin.period.${p}Short`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="card-content admin-chart-slot" style={{ height: '380px', padding: '10px 0' }}>
                      <ResponsiveContainer width="100%" height="100%" debounce={0}>
                        <AreaChart data={overviewChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                          <defs>
                            <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradComments" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px', fontSize: '12px' }} />
                          <Area type="monotone" name={t('admin.dashboard.orders')} dataKey="orders" stroke="#6366f1" strokeWidth={3} fill="url(#gradOrders)" animationDuration={1500} />
                          <Area type="monotone" name={t('admin.dashboard.comments')} dataKey="comments" stroke="#f59e0b" strokeWidth={3} fill="url(#gradComments)" animationDuration={1500} />
                          <Area type="monotone" name={t('admin.dashboard.users')} dataKey="users" stroke="#10b981" strokeWidth={3} fill="url(#gradUsers)" animationDuration={1500} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* График: Заказы (Теперь AreaChart) */}
                  <div className="bento-card bento-large" style={{ gridColumn: 'span 2' }}>
                    <div className="card-header">
                      <h3 className="card-title">
                        <div className="card-icon-circle"><Icon name="clock" size={14} /></div>
                        {t('admin.dashboard.charts.orders')}
                      </h3>
                      <div className="period-selector">
                        {['day', 'week', 'month', 'all'].map(p => (
                          <button
                            key={p}
                            className={`period-pill ${chartPeriod2 === p ? 'active' : ''}`}
                            onClick={() => setChartPeriod2(p)}
                          >
                            {t(`admin.period.${p}Short`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="card-content admin-chart-slot" style={{ height: '300px' }}>
                      <ResponsiveContainer width="100%" height="100%" debounce={0}>
                        <AreaChart data={ordersChartData}>
                          <defs>
                            <linearGradient id="gradOrdersSingle" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" name={t('admin.dashboard.orders')} dataKey="orders" stroke="#8b5cf6" strokeWidth={3} fill="url(#gradOrdersSingle)" animationDuration={1500} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* График: Пользователи */}
                  <div className="bento-card bento-large" style={{ gridColumn: 'span 2' }}>
                    <div className="card-header">
                      <h3 className="card-title">
                        <div className="card-icon-circle"><Icon name="users" size={14} /></div>
                        {t('admin.dashboard.charts.userStats')}
                      </h3>
                      <div className="period-selector">
                        {['day', 'week', 'month', 'all'].map(p => (
                          <button
                            key={p}
                            className={`period-pill ${chartPeriod3 === p ? 'active' : ''}`}
                            onClick={() => setChartPeriod3(p)}
                          >
                            {t(`admin.period.${p}Short`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="card-content admin-chart-slot" style={{ height: '300px' }}>
                      <ResponsiveContainer width="100%" height="100%" debounce={0}>
                        <AreaChart data={usersChartData}>
                          <defs>
                            <linearGradient id="gradUsersSingle" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" name={t('admin.dashboard.users')} dataKey="users" stroke="#10b981" strokeWidth={3} fill="url(#gradUsersSingle)" animationDuration={1500} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* График: Комментарии */}
                  <div className="bento-card bento-large" style={{ gridColumn: 'span 2' }}>
                    <div className="card-header">
                      <h3 className="card-title">
                        <div className="card-icon-circle"><Icon name="message-square" size={14} /></div>
                        {t('admin.dashboard.charts.commentStats')}
                      </h3>
                      <div className="period-selector">
                        {['day', 'week', 'month', 'all'].map(p => (
                          <button
                            key={p}
                            className={`period-pill ${chartPeriod4 === p ? 'active' : ''}`}
                            onClick={() => setChartPeriod4(p)}
                          >
                            {t(`admin.period.${p}Short`)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="card-content admin-chart-slot" style={{ height: '300px' }}>
                      <ResponsiveContainer width="100%" height="100%" debounce={0}>
                        <AreaChart data={commentsChartData}>
                          <defs>
                            <linearGradient id="gradCommentsSingle" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" name={t('admin.dashboard.comments')} dataKey="comments" stroke="#f59e0b" strokeWidth={3} fill="url(#gradCommentsSingle)" animationDuration={1500} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bento-card bento-large admin-category-stats-bento" style={{ gridColumn: 'span 2' }}>
                    <CategoryStats />
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'push' && (
              <motion.div
                key="push"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              >
                <AdminPushForm />
              </motion.div>
            )}

            {activeTab === 'create' && (
              <motion.div
                key="create"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="bento-grid-layout" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                  <div className="bento-card bento-large admin-create-employee-card premium-form-card" style={{ maxWidth: '650px', margin: '0 auto', width: '100%' }}>
                    <div className="card-header" style={{ padding: '24px 24px 16px' }}>
                      <h3 className="card-title" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        <div className="card-icon-circle" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', marginTop: '-2px' }}><Icon name="user-plus" size={16} /></div>
                        {t('admin.form.title')}
                      </h3>
                    </div>
                    
                    <form className="premium-form" onSubmit={handleSubmit} autoComplete="off">
                      <div className="card-content-rows">
                        <div className="form-row-premium horizontal">
                          <label className="row-label-caps">{t('admin.form.name')}</label>
                          <div className="row-value-right">
                            <div className="premium-input-wrapper" style={{ width: '100%', maxWidth: '400px' }}>
                              <Icon name="user" size={16} className="premium-input-icon" />
                              <input 
                                className="premium-input-minimal" 
                                name="newEmployeeFullName"
                                autoComplete="name"
                                value={formData.username}
                                onChange={handleChange} 
                                placeholder={t('admin.form.namePlaceholder')} 
                                required 
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-row-premium horizontal">
                          <label className="row-label-caps">
                            {t('admin.form.email')}{' '}
                            <span className="admin-form-optional">{t('admin.form.optional')}</span>
                          </label>
                          <div className="row-value-right">
                            <div className="premium-input-wrapper" style={{ width: '100%', maxWidth: '400px' }}>
                              <Icon name="mail" size={16} className="premium-input-icon" />
                              <input 
                                className="premium-input-minimal" 
                                name="email" 
                                autoComplete="off"
                                value={formData.email}
                                onChange={handleChange} 
                                placeholder={t('admin.form.emailPlaceholder')} 
                                type="email" 
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-row-premium horizontal">
                          <label className="row-label-caps">{t('admin.form.phone')}</label>
                          <div className="row-value-right">
                            <div className="premium-input-wrapper" style={{ width: '100%', maxWidth: '400px' }}>
                              <Icon name="phone" size={16} className="premium-input-icon" />
                              <input 
                                className="premium-input-minimal" 
                                name="phoneNumber" 
                                type="tel"
                                autoComplete="off"
                                value={formData.phoneNumber}
                                onChange={handleChange} 
                                placeholder={t('admin.form.phonePlaceholder')} 
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-row-premium horizontal">
                          <label className="row-label-caps">{t('admin.form.password')}</label>
                          <div className="row-value-right">
                            <div className="premium-input-wrapper premium-input-wrapper--password-toggle" style={{ width: '100%', maxWidth: '400px' }}>
                              <Icon name="lock" size={16} className="premium-input-icon" />
                              <input 
                                className="premium-input-minimal" 
                                name="newEmployeePassword" 
                                type={createEmployeePasswordVisible ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={formData.password}
                                onChange={handleChange} 
                                placeholder="••••••••" 
                                required
                              />
                              <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setCreateEmployeePasswordVisible((v) => !v)}
                                aria-label={createEmployeePasswordVisible ? t('login.hidePassword') : t('login.showPassword')}
                              >
                                <Icon name={createEmployeePasswordVisible ? 'eye-off' : 'eye'} size={18} />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="form-row-premium horizontal">
                          <label className="row-label-caps">{t('admin.form.role')}</label>
                          <div className="row-value-right">
                            <div style={{ width: '100%', maxWidth: '400px' }}>
                              <PremiumSelect
                                value={formData.role}
                                onChange={(val) => setFormData(prev => ({ ...prev, role: val }))}
                                options={creatableRoles.map((value) => ({
                                  value: value,
                                  label: t('users.roles.' + value),
                                  icon: value === 'DEV' ? 'zap' : value === 'ADMIN' ? 'shield' : value === 'SUPPORT' ? 'help-circle' : 'user'
                                }))}
                              />
                            </div>
                          </div>
                        </div>

                        {createEmployeeFormError && (
                          <div className="form-row-premium" style={{ borderBottom: 'none', padding: '0 24px 8px' }}>
                            <div className="form-status error" role="alert" style={{ width: '100%' }}>
                              {createEmployeeFormError}
                            </div>
                          </div>
                        )}
                        {createEmployeeSuccess && (
                          <div className="form-row-premium" style={{ borderBottom: 'none', padding: '0 24px 8px' }}>
                            <div className="form-status success" role="status" style={{ width: '100%' }}>
                              {t('admin.form.createSuccess')}
                            </div>
                          </div>
                        )}

                        <div className="form-row-premium" style={{ borderBottom: 'none', padding: '32px 24px' }}>
                          <div className="row-value-right" style={{ maxWidth: '100%', justifyContent: 'center' }}>
                            <button type="submit" className="btn-premium-action-new" disabled={createEmployeeSubmitting}>
                              <div className="btn-inner-glow" />
                              <Icon name="user-plus" size={18} />
                              <span>{createEmployeeSubmitting ? t('admin.form.submitting') : t('admin.form.submit')}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'promo' && isAdmin && <AdminPromoCodes key="promo" />}

            {activeTab === 'banners' && isDev && <AdminNewsBanners key="banners" />}

            {activeTab === 'marketplace' && isAdmin && <AdminMarketplace key="marketplace" isDev={isDev} />}
            {activeTab === 'public-files' && isDev && <AdminPublicFiles key="public-files" />}
            {activeTab === 'delete-user' && isDev && <AdminDeleteUser key="delete-user" />}
          </AnimatePresence>
        </main>

        <AnimatePresence mode="wait">
          {activeTab === 'stats' && (
            <motion.div
              key="stats-sidebar"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="profile-stats-sidebar"
            >
              <div className="admin-status-card-new">
                <div className="status-row">
                  <Icon name="users" size={20} />
                  <span className="status-label">{t('admin.dashboard.activeUsers')}</span>
                  <span className="status-value">{loadingStats ? '...' : Number(activeUsersCount ?? 0).toLocaleString()}</span>
                </div>
                <div className="status-divider" />
                <div className="status-row">
                  <Icon name="zap" size={20} />
                  <span className="status-label">API</span>
                  <span className="status-value">{loadingStats ? '...' : `${apiResponseTime} ${t('common.ms')}`}</span>
                </div>
              </div>

              <div className="profile-sidebar-metrics">
                <StatCard title={t('admin.dashboard.kpi.orders')} icon="shopping-cart" color="#3b82f6" entity="orders" />
                <StatCard title={t('admin.dashboard.kpi.users')} icon="user-plus" color="#10b981" entity="users" />
                <StatCard title={t('admin.dashboard.kpi.comments')} icon="message-square" color="#8b5cf6" entity="reviews" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {createEmployeeConfirmOpen && createPortal(
        <div
          className="admin-modal-backdrop"
          onClick={handleCloseCreateConfirm}
          role="presentation"
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-employee-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="create-employee-confirm-title" style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                {t('admin.form.confirmTitle')}
              </h3>
            </div>
            <div className="modal-body" style={{ color: 'var(--text-secondary)' }}>
              <p style={{ margin: '0 0 12px' }}>{t('admin.form.confirmSubtitle')}</p>
              <ul className="confirm-list">
                <li><strong>{t('admin.form.name')}:</strong> {formData.username.trim() || '—'}</li>
                <li><strong>{t('admin.form.phone')}:</strong> {formData.phoneNumber.trim() || '—'}</li>
                <li><strong>{t('admin.form.email')}:</strong> {(formData.email || '').trim() || t('admin.form.notSpecified')}</li>
                <li><strong>{t('admin.form.role')}:</strong> {t(`users.roles.${formData.role}`, formData.role)}</li>
              </ul>
              {createEmployeeApiError && (
                <div className="form-status error" role="alert" style={{ marginTop: 12 }}>
                  {createEmployeeApiError}
                </div>
              )}
            </div>
            <div className="modal-actions admin-modal-actions">
              <button
                type="button"
                className="btn btn-secondary admin-modal-btn-cancel"
                onClick={handleCloseCreateConfirm}
                disabled={createEmployeeSubmitting}
              >
                {t('admin.form.confirmCancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary admin-modal-btn-submit"
                onClick={handleConfirmCreateEmployee}
                disabled={createEmployeeSubmitting}
              >
                {createEmployeeSubmitting ? t('admin.form.submitting') : t('admin.form.confirmSubmit')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Admin;
